import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Joyride, STATUS, EVENTS } from 'react-joyride';
import { X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { track } from '../lib/analytics';

// Header et barre de navigation sont en `position: fixed` : isFixed garde le
// surlignage et l'infobulle accrochés à la cible même si la page défile.
const FIXED = { isFixed: true };

const STEPS_DESKTOP = [
    {
        target: 'body',
        placement: 'center',
        title: 'Bienvenue sur Trouve ton VIE 👋',
        content: "En 1 minute, on te montre comment ne rater aucune offre de VIE.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-offres"]',
        title: 'Toutes les offres Business France',
        content: "Toutes les offres VIE publiées sur Business France, mises à jour chaque jour. Filtre par pays, secteur ou durée.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-offresplus"]',
        title: "Prends de l'avance",
        content: "Les offres publiées sur les sites carrière des entreprises, souvent avant qu'elles n'arrivent sur Business France.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-entreprises"]',
        title: 'Explore par entreprise',
        content: "Découvre quelles entreprises recrutent le plus dans ton secteur ou dans le pays de tes rêves. Apprends aussi comment contacter les recruteurs pour leur soumettre ta candidature spontanée.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-notifications"]',
        title: 'Ne rate rien',
        content: "Tu es prévenu dès qu'une nouvelle offre correspond à tes préférences.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-favoris"]',
        title: 'Suis tes candidatures',
        content: "Sauvegarde les offres qui t'intéressent avec le ❤️ et coche celles où tu as postulé.",
    },
    {
        ...FIXED,
        target: '[data-tour="tour-nav-profil"]',
        title: 'Ton CV, ton meilleur atout',
        content: "Les offres sont souvent mal référencées ! Ajoute ton CV et tes préférences : le matching IA te propose alors les offres qui correspondent le mieux à tes compétences et tes attentes.",
    },
];

// Même parcours, ciblant la barre de navigation mobile. Pas de bell de
// notification visible sur mobile aujourd'hui : l'étape est retirée plutôt
// que de pointer vers rien.
const STEPS_MOBILE = [
    STEPS_DESKTOP[0],
    { ...STEPS_DESKTOP[1], target: '[data-tour="tour-nav-offres-mobile"]' },
    { ...STEPS_DESKTOP[2], target: '[data-tour="tour-nav-offresplus-mobile"]' },
    { ...STEPS_DESKTOP[3], target: '[data-tour="tour-nav-entreprises-mobile"]' },
    { ...STEPS_DESKTOP[5], target: '[data-tour="tour-nav-favoris-mobile"]' },
    { ...STEPS_DESKTOP[6], target: '[data-tour="tour-nav-profil-mobile"]' },
];

const JOYRIDE_OPTIONS = {
    zIndex: 300,
    skipBeacon: true,
    // Toutes les cibles sont dans le header / la nav fixes : pas besoin de scroller
    skipScroll: true,
    // La croix termine la visite (au lieu de passer à l'étape suivante par défaut)
    closeButtonAction: 'skip',
    // Un clic sur la zone assombrie ne fait rien (évite de quitter par erreur)
    overlayClickAction: false,
    overlayColor: 'rgba(15, 23, 42, 0.55)',
    spotlightPadding: 8,
    spotlightRadius: 12,
    arrowColor: '#ffffff',
};

const JOYRIDE_LOCALE = {
    back: 'Précédent',
    close: 'Fermer',
    last: 'Terminer',
    next: 'Suivant',
    skip: 'Passer la visite',
};

// Infobulle sur mesure (points de progression, bouton sombre, "Étape X sur N").
// Elle est rendue hors du <body> zoomé (voir usePortalOutsideBody), donc les
// tailles sont exprimées pour un rendu à 100 %.
const TourTooltip = ({ index, size, isLastStep, step, primaryProps, skipProps, closeProps, tooltipProps }) => {
    const primaryLabel = index === 0
        ? "C'est parti →"
        : isLastStep ? 'Ajouter mon CV →' : 'Suivant →';

    return (
        <div
            {...tooltipProps}
            className="pointer-events-auto w-[340px] max-w-[calc(100vw-32px)] bg-white rounded-3xl shadow-2xl shadow-slate-900/20 p-6 text-slate-900 font-sans"
        >
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-1.5" aria-hidden="true">
                    {Array.from({ length: size }).map((_, i) => (
                        <span
                            key={i}
                            className={`h-1.5 rounded-full transition-all duration-300 ${i === index
                                ? 'w-6 bg-slate-900'
                                : i < index ? 'w-1.5 bg-slate-400' : 'w-1.5 bg-slate-200'
                                }`}
                        />
                    ))}
                </div>
                <button
                    {...closeProps}
                    className="p-1 -m-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                    <X size={18} />
                </button>
            </div>

            {step.title && (
                <h3 className="text-lg font-black leading-snug mb-2">{step.title}</h3>
            )}
            <p className="text-sm text-slate-600 leading-relaxed">{step.content}</p>

            <div className="flex items-center justify-between gap-4 mt-6">
                {!isLastStep ? (
                    <button
                        {...skipProps}
                        className="text-sm font-semibold text-slate-500 hover:text-slate-800 whitespace-nowrap transition-colors"
                    >
                        Passer la visite
                    </button>
                ) : <span />}
                <button
                    {...primaryProps}
                    className="bg-slate-900 hover:bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full whitespace-nowrap transition-colors active:scale-[0.98]"
                >
                    {primaryLabel}
                </button>
            </div>

            <p className="text-right text-xs text-slate-400 mt-3">
                Étape {index + 1} sur {size}
            </p>
        </div>
    );
};

// Le site applique `zoom: 0.8` sur <body> (src/index.css). Joyride mesure les
// cibles en coordonnées écran puis dessine son overlay dans son portail : si ce
// portail est dans le <body> zoomé, tout est décalé d'un facteur 0,8. On monte
// donc le portail directement sous <html>, hors de toute zone zoomée.
const usePortalOutsideBody = () => {
    const [portal] = useState(() => {
        const el = document.createElement('div');
        el.id = 'onboarding-tour-portal';
        // Couche fixe à la taille de l'écran : Joyride dimensionne son overlay sur
        // ce conteneur. pointer-events: none pour ne jamais bloquer la page quand
        // la visite est inactive (l'overlay et l'infobulle réactivent les clics).
        Object.assign(el.style, {
            position: 'fixed',
            inset: '0',
            zIndex: '300',
            pointerEvents: 'none',
        });
        return el;
    });

    useEffect(() => {
        document.documentElement.appendChild(portal);
        return () => portal.remove();
    }, [portal]);

    return portal;
};

const OnboardingTour = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const portal = usePortalOutsideBody();
    const [run, setRun] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!user) return;

        let cancelled = false;
        const checkTourStatus = async () => {
            const { data } = await supabase
                .from('profiles')
                .select('onboarding_tour_seen')
                .eq('id', user.id)
                .single();

            if (!cancelled && data && data.onboarding_tour_seen === false) {
                // Petit délai pour laisser la page (et le header) finir de se monter
                setTimeout(() => {
                    if (!cancelled) {
                        setRun(true);
                        track('tour_started');
                    }
                }, 800);
            }
        };
        checkTourStatus();

        return () => { cancelled = true; };
    }, [user]);

    // Permet de relancer la visite manuellement (ex: lien "Revoir la visite" dans le profil)
    useEffect(() => {
        const handler = () => {
            setRun(true);
            track('tour_started', { restarted: true });
        };
        window.addEventListener('restart-onboarding-tour', handler);
        return () => window.removeEventListener('restart-onboarding-tour', handler);
    }, []);

    const markTourSeen = useCallback(async () => {
        if (!user) return;
        await supabase
            .from('profiles')
            .update({ onboarding_tour_seen: true })
            .eq('id', user.id);
    }, [user]);

    const handleEvent = useCallback((data) => {
        const { status, index, type } = data;

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            track('tour_step_viewed', { step: index });
        }

        if (status === STATUS.FINISHED) {
            setRun(false);
            markTourSeen();
            track('tour_completed');
            // La dernière étape parle du CV : on enchaîne directement sur son upload.
            navigate('/profil');
            window.dispatchEvent(new Event('open-cv-upload-modal'));
        } else if (status === STATUS.SKIPPED) {
            setRun(false);
            markTourSeen();
            track('tour_skipped', { step: index });
        }
    }, [markTourSeen, navigate]);

    if (!user) return null;

    return (
        <Joyride
            run={run}
            steps={isMobile ? STEPS_MOBILE : STEPS_DESKTOP}
            continuous
            onEvent={handleEvent}
            locale={JOYRIDE_LOCALE}
            options={JOYRIDE_OPTIONS}
            tooltipComponent={TourTooltip}
            portalElement={portal}
        />
    );
};

export default OnboardingTour;
